import { useCreate, useInvalidate } from '@refinedev/core';
import { Modal, Radio, Input, Space, App } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';
import { DecisionTag } from '../audit-log/decision-tag';

interface QueueRecord {
  id: string;
  timestamp: string;
  action: string;
  filter_decision: string | null;
  user_id: string | null;
}

const DECISIONS = ['false_positive', 'confirm_block', 'escalate_org_admin', 'escalate_community_admin'] as const;

export function ActionModal({
  record,
  open,
  onClose,
}: {
  record: QueueRecord | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const invalidate = useInvalidate();
  const [decision, setDecision] = useState<string>('');
  const [comment, setComment] = useState('');
  const createMutation = useCreate();
  const create = createMutation.mutate;

  const handleSubmit = () => {
    if (!record || !decision) return;
    create(
      {
        resource: 'moderation-decisions',
        values: {
          audit_log_id: record.id,
          decision,
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          notification.success({ message: t('moderationQueue.decisionSuccess') });
          // Refresh both surfaces: the dedicated queue and the unified
          // audit-log list where the action can now also be taken.
          invalidate({ resource: 'moderation-queue', invalidates: ['list'] });
          invalidate({ resource: 'audit-log', invalidates: ['list'] });
          resetAndClose();
        },
        onError: (err) => {
          if (err?.statusCode === 409) {
            notification.warning({ message: t('moderationQueue.alreadyDecided') });
            resetAndClose();
          } else {
            notification.error({ message: err?.message ?? t('errors.generic') });
          }
        },
      },
    );
  };

  const resetAndClose = () => {
    setDecision('');
    setComment('');
    onClose();
  };

  return (
    <Modal
      title={t('moderationQueue.modal.title')}
      open={open}
      onCancel={resetAndClose}
      onOk={handleSubmit}
      okText={t('moderationQueue.modal.submit')}
      okButtonProps={{ disabled: !decision }}
    >
      {record && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <div>{formatDateBidi(record.timestamp)}</div>
          <div><BidiText>{record.action}</BidiText></div>
          <div><DecisionTag value={record.filter_decision} /></div>
          {record.user_id && <MonoCode>{record.user_id.slice(0, 8)}…</MonoCode>}
        </Space>
      )}
      <Radio.Group
        value={decision}
        onChange={(e) => setDecision(e.target.value)}
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {DECISIONS.map((d) => (
          <Radio key={d} value={d}>
            {t(`moderationQueue.decision.${d}`)}
          </Radio>
        ))}
      </Radio.Group>
      <div style={{ marginTop: 16 }}>
        <label>{t('moderationQueue.modal.comment')}</label>
        <Input.TextArea
          maxLength={2000}
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>
    </Modal>
  );
}
